import { useCallback, useMemo, useState } from 'react';
import { closestIndex } from '../../lib/frames';

const EMPTY = [];

/**
 * Which of each camera's frames the panels show: the newest by default, or
 * one moment the operator has stepped back to. The two panels move
 * together -- pressing an arrow on either camera picks a frame of that
 * camera, and every other camera shows the frame it has closest to that
 * capture time, so both views are of the same moment (or the nearest one
 * the other camera made). Tracked separately for stills and videos.
 *
 * The selection is a camera id plus a filename, not an index, and null
 * means "the newest, whatever that is". So while nothing is selected the
 * panels follow live updates, and once something is, a new arrival grows the
 * lists without yanking the view -- the chosen moment stays put. Stepping
 * forward onto a camera's newest frame clears the selection, which is what
 * puts every panel back on live. A selected frame that disappears from the
 * listing falls back to live as well.
 *
 * @param {{ cameraId: number, photo: Frame[], video: Frame[] }[]} cameras
 * @param {'photo' | 'video'} mode
 * @returns {Record<number, View>} keyed by camera id, where a View is
 *   { frame, index, count, atOldest, atNewest, isLive, older, newer }
 */
export function useFrameBrowser(cameras, mode) {
  const [selected, setSelected] = useState({ photo: null, video: null });
  const chosen = selected[mode];

  const select = useCallback(
    (cameraId, filename) =>
      setSelected((old) => ({ ...old, [mode]: filename ? { cameraId, filename } : null })),
    [mode]
  );

  return useMemo(() => {
    const framesOf = (cam) => cam?.[mode] ?? EMPTY;
    const anchorCam = chosen ? cameras.find((c) => c.cameraId === chosen.cameraId) : null;
    const anchor = anchorCam ? framesOf(anchorCam).find((f) => f.filename === chosen.filename) : null;
    const anchorT = anchor ? anchor.takenAt.getTime() : null;

    const views = {};
    for (const cam of cameras) {
      const frames = framesOf(cam);
      let index = frames.length - 1;
      if (anchorT != null) {
        index =
          cam.cameraId === chosen.cameraId
            ? frames.indexOf(anchor)
            : closestIndex(frames, anchorT);
      }

      views[cam.cameraId] = {
        frame: frames[index] ?? null,
        index,
        count: frames.length,
        atOldest: index <= 0,
        atNewest: index >= frames.length - 1,
        isLive: anchorT == null,
        older: () => {
          if (index > 0) select(cam.cameraId, frames[index - 1].filename);
        },
        newer: () => {
          // Landing on the newest frame means going live, not pinning it.
          if (index >= frames.length - 2) select(cam.cameraId, null);
          else select(cam.cameraId, frames[index + 1].filename);
        },
      };
    }
    return views;
  }, [cameras, mode, chosen, select]);
}
